/**
 * View models rendered by the epi-chat feature. These are derived from the
 * wire AG-UI event stream by {@link useEpiChatStream} and are intentionally
 * decoupled from the on-wire discriminated union.
 */

/**
 * 3-surface chat routing classification (mirrors backend ``Surface`` enum and
 * the wire-level ``AgUiSurface`` on ``AgUiEvent``).
 *
 * - `main`   — durable-for-turn, user-audience prose: progress and decisions.
 *              Rendered inline in the message stream at full weight.
 * - `drill`  — process detail (sub-agent tool calls, raw payloads, reasoning).
 *              Hidden behind the per-tool "show internal steps" toggle and
 *              suppressed entirely when the global drill-in switch is off.
 * - `pinned` — decisions + rationale that outlive the chat scrollback,
 *              attached to the artifact they affected (Decisions tab on the
 *              right-side drawer). Not implemented in the scaffold; reserved.
 */
export type EpiSurface = 'main' | 'progress' | 'drill' | 'pinned';

export type EpiRunState = 'idle' | 'streaming' | 'awaiting_input' | 'error';

export type EpiMessageRole = 'user' | 'assistant' | 'system';

/**
 * A message body is a sequence of parts so that tool calls and clarifying-question
 * cards can render inline, in order, between prose chunks.
 */
export type EpiMessagePart =
  | { kind: 'text'; text: string }
  | { kind: 'reasoning'; text: string }
  | { kind: 'tool_call'; toolCall: EpiToolCall }
  | { kind: 'clarifying_question'; question: EpiClarifyingQuestion }
  | { kind: 'checkpoint'; checkpoint: EpiCheckpoint }
  | { kind: 'av_selection_checkpoint'; checkpoint: AvSelectionCheckpoint }
  | { kind: 'artifact_ref'; artifactRef: EpiArtifactRef }
  | { kind: 'resource_ref'; resourceRef: EpiResourceRef }
  | { kind: 'uploaded_file'; uploadedFile: EpiUploadedFile }
  | { kind: 'attachment'; attachment: EpiAttachment }
  | { kind: 'notice'; severity: 'error' | 'warning' | 'info'; message: string };

/**
 * A file the researcher attached to their own message — a reference, not contents.
 * Distinct from {@link EpiUploadedFile}, the agent-side ingested-file chip.
 */
export interface EpiAttachment {
  fileId: string;
  filename: string;
  mimeType: string;
  size: number;
}

/** An attachment mid-upload. Becomes an {@link EpiAttachment} once `ready`. */
export interface EpiStagedAttachment {
  /** Client-minted, stable across the upload so the pill can be tracked and removed. */
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  status: 'uploading' | 'ready' | 'error';
  /** Server-minted; present once `status` is `ready`. */
  fileId?: string;
  /** Researcher-facing failure reason; present when `status` is `error`. */
  error?: string;
}

/**
 * A file the researcher uploaded into the chat (OR-6125). Rendered as a
 * distinct compact chip — NOT the {@link EpiResourceRef} artifact card — so
 * the user can see what they attached and re-download the original. The
 * download streams from the auth'd `/concept-groups/uploads/{uploadId}/source`
 * endpoint, so the chip carries the `uploadId` (download key — scoped to the
 * upload, so it works even for an unrecognized file with no preview) + the
 * `fileName` (display + download name).
 */
export interface EpiUploadedFile {
  fileName: string;
  uploadId: string;
}

/**
 * Inline message-stream card pointing at an artifact the agent just
 * created (or re-emitted on resume). Renders as a clickable affordance —
 * the user clicks it to focus the artifact in the right-hand drawer
 * instead of having the orchestrator dump the artifact's content into
 * chat. Decouples chat-as-narration from drawer-as-system-of-record:
 * the chat shows "we did this", the drawer holds the detail.
 */
export interface EpiArtifactRef {
  artifactId: string;
  /** Mirrored from the artifact for friction-free render — display label + id (the slug value) + kind. */
  dataElement: string;
  name?: string;
  kind?: EpiArtifactKind;
  /** Wall-clock when the artifact landed; used for the chip's timestamp. */
  createdAt: string;
}

/**
 * Resource kinds the chat-card surface knows how to render and route to.
 * Mirrors the BE `RESOURCE_CARD` payload's `resourceKind` enum.
 */
export type EpiResourceKind = 'cohort' | 'analytical_variable' | 'concept_group' | 'analysis';

/**
 * Lifecycle status of a resource card. Drives the status pill and the
 * disabled-vs-clickable affordance.
 *
 * - `planning` — agent has committed to working on this resource (e.g.
 *   library search returned no match for an analytical variable; agent is about to
 *   draft it) but no sub-agent is writing yet. Card is clickable and
 *   routes to a skeleton surface that says "Setting up…". Gives the
 *   user a stable nav handle the moment the agent picks up the task,
 *   rather than waiting through clarifying questions / bindings
 *   checkpoint before any card appears.
 * - `drafting` — sub-agent is actively writing the artifact (e.g.
 *   ``av_drafter`` running). Same click target as ``planning``.
 * - `updating` — a subsequent agent tool call is mutating an existing
 *   resource. Click routes to the live detail page.
 * - `ready` — server-side commit landed; card is fully clickable and
 *   the route points at the canonical detail page.
 * - `error` — the originating tool call failed. Card surfaces the
 *   error state and does not navigate.
 */
export type EpiResourceCardStatus = 'planning' | 'drafting' | 'updating' | 'ready' | 'error';

/**
 * Inline chat card pointing at a project resource (cohort / analytical variable / concept
 * group / analysis) the agent has created or is mutating. Sibling to
 * {@link EpiArtifactRef}: that one opens the right-hand drawer; this one
 * navigates to a TanStack-driven detail page via
 * `useChatPreservingNavigate`.
 *
 * Identity ratchets across the card's lifetime: the analytical variable draft path emits
 * successive cards keyed first on `clientId` (pre-manifest), then on
 * `resourceId` once the slug exists. The reducer matches on
 * `(resourceId || clientId)` and refreshes the same slot in place.
 * Cohorts mint a real `resourceId` on the first commit and never ratchet.
 *
 * `hintArgs` carries optimistic detail from the originating tool args
 * (e.g. the chosen description) so the card shows useful content before
 * the canonical server fetch lands.
 */
export interface EpiResourceRef {
  resourceKind: EpiResourceKind;
  /** Stable id once known (`cohort_id`, `av.<slug>`, library UUID, …). */
  resourceId?: string;
  /** Pre-persistence id used during the drafting window. */
  clientId?: string;
  projectId: string;
  name: string;
  status: EpiResourceCardStatus;
  /** BE-computed route the card click navigates to. */
  route: string;
  /** Optimistic detail from the originating tool args. */
  hintArgs?: Record<string, unknown>;
  /** First-emit wall-clock; preserved across in-place refreshes. */
  createdAt: string;
  /** Most-recent emit wall-clock; bumps on every refresh. */
  updatedAt: string;
}

export interface EpiMessage {
  id: string;
  role: EpiMessageRole;
  parts: EpiMessagePart[];
  status: 'streaming' | 'complete' | 'error';
  timestamp: string;
}

/**
 * Heterogeneous timeline of a sub-agent's internal activity, in arrival
 * order. Tool calls and reasoning chunks share one chronological list so
 * the drill-down renders as a coherent trail (alternating tool ▸ thought
 * ▸ tool ▸ thought) instead of two parallel axes.
 *
 * Reasoning entries are sub-agent text rerouted via STATUS_UPDATE events
 * (``surface=drill`` with a ``parentEventId``). Tool entries reference
 * the corresponding ``EpiToolCall`` by its ``toolId`` — the call body
 * still lives on the parent's ``subCalls`` so per-tool render logic
 * (status mark, args, result expansion) can find it without traversal.
 */
export type EpiInternalLogEntry =
  | { kind: 'tool_call'; toolId: string }
  | { kind: 'reasoning'; id: string; text: string };

export interface EpiToolCall {
  toolId: string;
  toolName: string;
  friendlyLabel: string;
  args: Record<string, unknown>;
  result?: unknown;
  status: 'running' | 'complete' | 'error';
  /**
   * Identifier of the agent that produced this tool call. Top-level tools
   * carry ``orchestrator``; sub-agent internal tool calls carry the
   * sub-agent name (e.g. ``av_drafter``). Used to label nested tool groups
   * in the UI ("Internal steps in av_drafter").
   */
  agentId?: string;
  /**
   * Routing surface assigned by the backend (or defaulted to ``main`` when
   * absent). Sub-agent internal tool calls land here as ``drill``; the
   * global drill-in toggle suppresses everything tagged ``drill`` when off.
   */
  surface?: EpiSurface;
  /**
   * Nested tool calls made by a sub-agent beneath this tool. Populated by
   * the reducer when the backend surfaces sub-agent callback events tagged
   * with ``parentEventId`` matching this tool's ``toolId``. Carries the
   * full call body (args, result, status) — the ``internalLog`` references
   * these by ``toolId`` for chronological render order.
   */
  subCalls?: EpiToolCall[];
  /**
   * Sub-agent activity timeline — tool calls and reasoning chunks
   * interleaved in arrival order. When non-empty the drill-down renders
   * from this list (so reasoning lands between the tool calls it
   * narrates) rather than from ``subCalls`` alone. Populated alongside
   * ``subCalls`` by the reducer; older flows that don't emit reasoning
   * still get a tool-only trail.
   */
  internalLog?: EpiInternalLogEntry[];
  /**
   * OR-6324/OR-6328 — plain-language note set when the backend reported
   * this call as a best-effort failure (``STATUS_UPDATE
   * status=tool_failure_best_effort``): the tool failed but the assistant
   * continued without it. Rendered as a muted warning line under the row in
   * both feature-flag states; deliberately does NOT flip ``status`` to
   * ``error`` — best-effort means the turn kept going, and the group
   * header's last-call-error rule must not fire for it.
   */
  bestEffortFailureNote?: string;
}

export interface EpiClarifyingQuestionOption {
  label: string;
  description?: string;
  value?: string;
  /**
   * When the option is a candidate concept group (a disambiguation between
   * existing groups), the group's id — lets the card render a "View" drill-in
   * affordance so the researcher can inspect the group's contents before
   * choosing. Absent for plain (non-concept-group) options.
   */
  conceptGroupId?: string | null;
}

export interface EpiClarifyingQuestion {
  questionId: string;
  question: string;
  options: EpiClarifyingQuestionOption[];
  allowCustom: boolean;
  answered: boolean;
  answerText?: string;
}

/**
 * Workflow-phase gates the orchestrator pauses at for epi approval. The
 * backend emits one of these as a STATUS_UPDATE with `kind: 'checkpoint'` and
 * blocks until the epi sends a new user turn (approve / edit / redirect).
 */
export type EpiCheckpointStage =
  | 'intent'
  | 'bindings'
  | 'draft'
  | 'validation'
  | 'judge'
  /**
   * Cohort plan confirmation gate (OR-6037, Checkpoint 2). Emitted after the
   * cohort_drafter sub-agent commits its plan; the user reviews stubs
   * (role + plain-language description), optionally edits them inline, and
   * approves before the per-stub resolve loop starts.
   */
  | 'cohort_plan'
  /**
   * Concept-group create-consent gate (OR-6271 / OR-6124). Emitted when no
   * existing library group fits a term and the agent needs the researcher's
   * yes/no before creating a NEW group — a short consent, NOT a proposal. The
   * agent runs `propose_concept_group` only after this is approved.
   */
  | 'concept_group'
  /**
   * Analysis processing gate. Emitted when an analysis is ready to run; the
   * researcher starts processing with a "Process" button. Carries no structured
   * payload — the gated analysis is named by ``assetDisplayName``.
   */
  | 'process_analysis';

/**
 * Citation entry the agent attaches to a checkpoint when a recalled
 * knowledge entry materially shaped its summary. The UI renders these as
 * dismissible chips so the epi can reject any inherited assumption — most
 * importantly, ones whose `source` references a different past session
 * (the "memory bleed" case).
 */
export interface EpiAppliedKnowledge {
  id: string;
  kind: string;
  scope: string;
  source: string;
  title: string;
  /**
   * Knowledge-store granularity. When ``granularity === 'global_rule'``
   * the entry is an operational standing rule (warehouse quirk, agent
   * behavior, schema-introspection convention) — not a domain finding the
   * epi is meant to evaluate. The renderer suppresses these from
   * citation chips so the strip stays focused on choices the epi can
   * actually agree or disagree with. Curated rules have ids prefixed
   * with ``global.`` and live in `epi_study_agent.knowledge.global_findings`.
   */
  granularity?: string;
}

/**
 * True for knowledge entries that are operational standing rules rather
 * than domain findings the epi can act on. Used to suppress them from
 * the checkpoint's applied-knowledge strip and the artifact drawer's
 * citations chip — the agent must still recall and apply them, but the
 * epi has no agency over them so citing them adds noise. Two signals:
 * curated entries declare ``granularity='global_rule'`` explicitly; the
 * id-prefix fallback handles wire payloads that didn't propagate the
 * granularity field (defense in depth).
 */
export const isOperationalGlobalRule = (entry: EpiAppliedKnowledge): boolean =>
  entry.granularity === 'global_rule' || entry.id.startsWith('global.');

/**
 * One planned criterion on a cohort plan card. Mirrors the backend
 * ``CohortPlanStub`` (``epi_study_agent/agents/cohort_drafter.py``).
 */
export type CohortPlanStubRole = 'index' | 'inclusion' | 'exclusion';

export interface CohortPlanStub {
  role: CohortPlanStubRole;
  /**
   * Plain-language intent for this criterion. Editable inline on the plan
   * card; used in the post-Checkpoint-2 resolve loop as the analytical variable search
   * query / draft intent, so edits here directly steer what the agent
   * builds.
   */
  description: string;
}

export interface CohortPlanStudyPeriod {
  startDate: string | null;
  endDate: string | null;
  description: string | null;
}

/**
 * Structured plan payload carried on ``stage='cohort_plan'`` checkpoints.
 * Mirrors the backend ``CohortPlan`` Pydantic model.
 */
export interface CohortPlan {
  cohortName: string;
  cohortDescription: string;
  studyPeriod: CohortPlanStudyPeriod | null;
  stubs: CohortPlanStub[];
}

/**
 * A single proposed concept group on the unified intent checkpoint (OR-6018).
 * Sourced from the backend selector (``staged_bindings``) — NOT the LLM echo —
 * so ids are reliable and ``clinicalTerm`` is the stable per-row swap identity.
 */
export interface UnifiedIntentConceptGroup {
  /** The clinical term this concept group answers — stable row/swap identity. */
  clinicalTerm: string;
  /** The kind of clinical concept the term maps to (e.g. Condition, Drug). */
  domainHint: string | null;
  /** Resolution status (selected_from_library | unmatched | needs_approval | …). */
  status: string | null;
  conceptGroupId: string | null;
  name: string | null;
  /** Catalog-resolved owner ("Verantos" for a curated group), for chip provenance. */
  owner: string | null;
  domains: string[];
}

/** One concept group bound to a concept-mapping term (the curated path). */
export interface ConceptMappingGroup {
  conceptGroupId: string;
  name: string;
  owner: string | null;
  domains: string[];
}

/**
 * One inline standard OMOP concept bound to a concept-mapping term — the
 * ad-hoc-id path (e.g. gender = Male (8507)). Inline concepts must be standard.
 * Populated in a later slice; the shape exists now so the wire is stable.
 */
export interface InlineConcept {
  conceptId: number;
  name: string;
  standard: boolean;
}

/**
 * One row of the analytical-variable intent (OR-6018): a clinical term and the
 * OMOP concepts it resolves to, scoped to the filter it will be used in. A
 * mapping is EITHER concept groups (``type='concept_group'``) OR inline standard
 * concepts (``type='inline_concept'``) — never both. ``table`` / ``column`` are
 * the OMOP entity + concept column the term filters against; ``domain`` is
 * derived from that pair and scopes which concept groups are offered.
 * ``mappingId`` is the stable per-row identity (NOT the term — terms may repeat
 * across mappings for different filter contexts).
 */
export interface ConceptMapping {
  mappingId: string;
  clinicalTerm: string;
  table: string | null;
  column: string | null;
  domain: string | null;
  type: 'concept_group' | 'inline_concept';
  conceptGroups: ConceptMappingGroup[];
  inlineConcepts: InlineConcept[];
  status: string | null;
}

/**
 * Structured payload carried on the unified ``stage='intent'`` checkpoint
 * (OR-6018) — the single confirmation that folds the legacy intent → bindings
 * two-hop. ``conceptMappings`` / ``conceptGroups`` are backend-sourced;
 * ``definition`` / ``format`` / ``usedIn`` are the agent's proposal. Absent
 * payload (``null``) ⇒ a legacy intent checkpoint, which renders through the
 * generic ``CheckpointCard``.
 *
 * ``conceptMappings`` is the structured shape the card moves to (term → 1-many
 * concept groups or inline concepts, with table/column/domain). It is always
 * populated by the parser — from the backend ``concept_mappings`` when present,
 * else synthesized from the legacy ``conceptGroups``. ``conceptGroups`` is kept
 * until the card consumes ``conceptMappings``.
 */
export interface UnifiedIntentPayload {
  definition: string | null;
  format: string | null;
  usedIn: string | null;
  conceptGroups: UnifiedIntentConceptGroup[];
  conceptMappings: ConceptMapping[];
}

export interface EpiCheckpoint {
  checkpointId: string;
  stage: EpiCheckpointStage;
  /** 2-4 sentence summary (markdown) the epi is expected to read. */
  summary: string;
  /**
   * OR-6448: researcher-facing disclosures composed by the platform (not the
   * model) — e.g. "the requirement X is already enforced by the index date"
   * when the plan check removed an entailed criterion. Rendered verbatim by
   * the approve prompt, whose review line is FE i18n copy that never shows
   * ``summary``. Also appended into ``summary`` for the card renderers, so
   * a consumer reads ONE of the two, never both.
   */
  // TODO: cleanup with research_assistant_criterion_redundancy_guard ff
  summaryNotes?: string[];
  /** One-line preview of the tool call the agent would make if approved. */
  proposedNext: string;
  /**
   * Knowledge entries the agent applied while preparing this checkpoint.
   * Required-by-convention on `stage='intent'` whenever a `recall_knowledge`
   * hit influenced the summary. Empty array = "I didn't lean on any retrieved
   * knowledge for this gate."
   */
  appliedKnowledge: EpiAppliedKnowledge[];
  /**
   * Stage-specific payload riding alongside ``summary`` / ``proposedNext``.
   * Opaque at the envelope level — consumers narrow by ``stage``:
   *   - ``stage === 'cohort_plan'`` → ``payload`` is a {@link CohortPlan}.
   *   - Other stages → ``payload`` is ``null`` today.
   * New stages add to the ``CHECKPOINT_PAYLOAD_PARSERS`` registry in
   * ``useEpiChatStream`` instead of growing this interface.
   */
  payload: unknown;
  /**
   * Identity of the asset this gate checkpoint approves, carried on the
   * envelope by the slim `request_approval` tool (OR-6357 issue 8). Lets the
   * review-and-approve prompt name the exact gated asset instead of guessing
   * from sibling cards (issue 6). Absent on pre-OR-6357 payloads (replay) and
   * on non-gate stages — renderers fall back to the sibling-card lookup.
   */
  assetKind?: 'analytical_variable' | 'cohort' | 'analysis';
  assetDisplayName?: string;
  /**
   * OR-6368: whether this gate follows a create (auto-surfaced by the drafting
   * tool) or an edit (`request_approval` re-surface) — drives the review line's
   * "I've created …" vs "I've updated …" copy. `mixed` marks a combined card
   * that approves both created and updated drafts. Absent on legacy payloads.
   */
  assetOperation?: 'created' | 'updated' | 'mixed';
  /**
   * Number of analytical-variable drafts this gate approves. > 1 marks
   * a COMBINED "Approve all" checkpoint (the backend coalesced N create gates
   * surfaced in one turn); the review prompt then uses count-based copy and a
   * single Approve stamps every member. Absent / 1 → a normal single-asset gate.
   */
  assetCount?: number;
  /**
   * OR-6504: present when this gate approves a parameterized variable FAMILY —
   * several variables sharing one definition and differing in exactly one thing.
   * Only ONE draft exists at this point (the exemplar); the rest are created
   * after approval. Drives its own review copy and the parameter-value list, and
   * must take precedence over `assetCount`, whose copy would claim N variables
   * were already created.
   */
  family?: EpiVariableFamily;
  answered: boolean;
  answerText?: string;
}

/**
 * OR-6504: the family block carried on a variable-family approval gate — the
 * generalized definition plus every value the set will be built from. This is
 * what the researcher reviews before approving, so it is rendered on the card
 * rather than derived from the (single) draft that exists.
 */
export interface EpiVariableFamily {
  familyId: string;
  /**
   * The group's own title, which is not any member's display name — it heads the
   * card, the group panel and the draft-stage list row. Falls back to the
   * generalized definition rather than ever being empty.
   */
  displayName: string;
  /**
   * The shared shape of the members' aliases (e.g. `baseline_{diagnosis}`),
   * recovered from the aliases for display only. Absent when there is no pattern
   * worth showing — including when a mint-time rename broke it — in which case
   * the surface shows the aliases themselves.
   */
  aliasPattern?: string;
  /** What varies, in the researcher's words (e.g. "symptom"). */
  parameterLabel: string;
  /** The shared definition with the varying part named (e.g. "… of {symptom}"). */
  generalizedDescription: string;
  /** How many variables the set will contain — always the true total. */
  memberCount: number;
  /** The output shape every member shares (boolean/numeric/string/date). */
  outputType?: string;
  /** What the shared output means, when the model supplied it. */
  outputValues?: string;
  /**
   * The mapping pool, shown once with the members that reference each term
   * rather than repeated per variable. The two parameter kinds populate
   * `usedIn` inversely: a concept-group family gives each varying term one
   * member, while a window family's terms are shared by all of them.
   */
  conceptMappings: EpiVariableFamilyMapping[];
  members: EpiVariableFamilyMember[];
}

/** One row of a family's shared mapping pool. */
export interface EpiVariableFamilyMapping {
  /** The clinical term as the researcher named it. */
  clinicalTerm: string;
  /** The concept groups it resolved to. */
  conceptGroups: { conceptGroupId: string; name: string }[];
  /** 1-based member positions that reference this term, matching the numbered list. */
  usedIn: number[];
  /** True when every member shares it (an invariant mapping). */
  shared: boolean;
}

/** One prospective member of a variable family. */
export interface EpiVariableFamilyMember {
  /** The varying value, in the researcher's words (e.g. "Hallucinations"). */
  parameterValue: string;
  displayName: string;
  alias: string;
  /** The one member that exists as a reviewable draft right now. */
  isExemplar: boolean;
  /**
   * The assessment window's structure, for a time_window family only. Present so
   * the review surface can state which side of the index date the window sits on
   * — "90 days after the index date" rather than "90 days" — instead of leaving
   * the researcher to infer it from the shared description. Absent for a
   * concept-group family, and `direction` is absent on specs proposed before that
   * field existed.
   */
  window?: {
    value: number;
    unit: string;
    direction?: string;
  };
}

/**
 * A single library analytical variable candidate surfaced by the analytical variable library search sub-agent.
 * `kind` distinguishes stored IR-based analytical variables from de-novo drafts saved to the library.
 */
export interface AvSelectionCandidate {
  analyticalVariableId: string;
  name: string;
  dataElement: string;
  kind: 'av_ir' | 'de_novo';
  reason?: string;
}

/**
 * analytical variable library selection checkpoint surfaced when the sub-agent finds one or
 * more library matches. The epi picks the primary, picks an alternative, or
 * chooses "build new". Carried as `kind: 'av_selection_checkpoint'` in the
 * message part stream.
 */
export interface AvSelectionCheckpoint {
  checkpointId: string;
  summary: string;
  /**
   * The researcher's requested variable as a short plain-language phrase,
   * rendered in the card's headline sentence ("N existing variables may match
   * <requestedVariable>."). Absent on payloads emitted before OR-6338 —
   * renderers fall back to the `summary` prose.
   */
  requestedVariable?: string;
  primary: AvSelectionCandidate | null;
  alternatives: AvSelectionCandidate[];
  allowBuildNew: boolean;
  /**
   * Present only when the card is asking which existing variable should be
   * followed as a structural pattern for the drafter — not a variable to adopt,
   * and not necessarily for a brand-new variable (a redraft of an existing one
   * can reference a pattern too). Absent (the common case, and every payload
   * emitted before reference mode existed) means the normal "reuse this existing variable?"
   * card, so replayed sessions render exactly as they always have.
   */
  purpose?: 'search_reference_variable';
  answered: boolean;
  answerText?: string;
}

export interface EpiChatSendPayload {
  text: string;
  /** Set when the message is an answer to a clarifying question. */
  questionId?: string;
  choice?: string;
  /** Set when the message is an approval/edit/redirect for a checkpoint gate. */
  checkpointId?: string;
  /**
   * Caller-supplied client id. Lets a message already rendered optimistically
   * elsewhere (e.g. a new-chat seed) be sent for real without minting a
   * second id and duplicating the bubble — see ``userMessageSent``'s dedupe
   * guard. Omitted lets the sender mint a fresh id as before.
   */
  id?: string;
  /** References to files already stored. Omitted when nothing is attached. */
  attachments?: EpiAttachment[];
}

/**
 * Artifact kinds the right-side drawer knows how to render.
 *
 * - `av` — a legacy DSL-defined Analytical Variable. The logic body is a
 *   structured tree (anchor / temporal / filter group). Rendered with the
 *   analytical variable-shape tab set: Description / SQL / Verification / Results.
 * - `av_denovo` — a SQL-first Analytical Variable authored by the agent
 *   directly (no DSL tree, no structured logic). Same tab set as `av`; the
 *   distinction matters only for upstream tools that decide how to mutate
 *   the artifact (de novo analytical variables are edited as text, DSL analytical variables through structured
 *   operations).
 * - `composition` — a higher-order analytical variable that anchors on / composes other analytical variables.
 * - `concept_group` — a clinical concept group (codeset). Cross-study
 *   shared. Rendered with the concept-group tab set: Description / Codes /
 *   Usage; Definition tab is added when the group has a definition body
 *   (rule-based concept groups carry a SQL/markdown definition; flat
 *   codesets do not).
 */
export type EpiArtifactKind = 'av' | 'av_denovo' | 'composition' | 'concept_group';
export type EpiArtifactStatus = 'drafting' | 'validated' | 'blocked';
export type EpiArtifactOutputType = 'boolean' | 'numeric' | 'string' | 'date';
export type EpiArtifactOutputValueType =
  | 'boolean'
  | 'signed_integer'
  | 'unsigned_integer'
  | 'unsigned_decimal'
  | 'signed_decimal'
  | 'string'
  | 'date';

/**
 * Tabs surfaced on the right-side artifact drawer. The set rendered depends
 * on the artifact's `kind` — see {@link tabsForArtifactKind} in the drawer.
 *
 * The agent's tool calls map to tabs so the drawer auto-focuses the surface
 * a tool just produced — e.g. `verify_analytical_variable` → `verification`, `compute_results`
 * → `results`. See `TOOL_TAB_MAP` in the stream reducer.
 */
export type EpiArtifactTab =
  | 'description'
  | 'sql'
  | 'verification'
  | 'results'
  | 'decisions'
  | 'recall'
  | 'definition'
  | 'codes'
  | 'concepts'
  | 'usage';

/**
 * Drafter-time design decision row (mirrors backend `DesignDecision`).
 * Each row pairs a ≤8-word decision label with its rationale. Rendered
 * as a flat table on the Decisions tab — the durable audit trail of why
 * the SQL is shaped the way it is. Examples:
 *   - decision="PERSON spine, not COHORT", rationale="Standing rule …"
 *   - decision="NULL for non-qualifiers", rationale="Downstream filters …"
 */
export interface EpiDesignDecision {
  decision: string;
  rationale: string;
}

/**
 * Structured clinical breakdown that backs the Description tab. Each
 * field is a free-text section in clinical language; the renderer drops
 * any section that's empty rather than printing a blank header. The
 * shape mirrors `ClinicalExplanation` on the backend manifest exactly —
 * snake_case field names converted to camelCase at the wire boundary.
 *
 * Sourced from `manifest.clinical_explanation` which is the single
 * source of truth for the variable's clinical contract. The previous
 * setup also derived `avs/<slug>/explain.md` from the same data and
 * shipped that pre-rendered markdown to the UI, but that produced two
 * encodings of the same content; the frontend now renders sections
 * directly from the structured object.
 */
export interface EpiClinicalExplanation {
  /** "What this measures" — opening framing with the bolded core concept. */
  answers?: string;
  /** What the value column represents — type, units, semantics. */
  value?: string;
  /** What counts as a qualifying event — markdown table preferred. */
  qualifyingCriteria?: string;
  /** Window applied + clinical reason for it. */
  timeWindow?: string;
  /** Tie-breaking among multiple candidates — omit when not applicable. */
  valueSelection?: string;
  /** Who gets NULL and how to interpret it. */
  missingValues?: string;
  /** Optional trailer — what this variable is NOT. */
  outOfScope?: string;
}

/**
 * Drafter-time decision for one concept-group binding on an analytical variable. Surfaced
 * on the Concepts tab so an epi can audit *why* the agent picked a
 * particular group and what alternatives it weighed before committing.
 *
 * ``conceptGroupId`` is a stable id (the same shape Markdown.tsx auto-
 * links) so the tab can route clicks to the concept-group details
 * dialog. ``status`` distinguishes the three pathways the agent has:
 *   - ``curated``  — picked an existing catalog group (most common).
 *   - ``proposed`` — built a new group from seeds + rules.
 *   - ``inline``   — small ad-hoc id list (no group exists; e.g. gender=Male).
 *
 * ``rationale`` is short prose; ``alternativesConsidered`` lists groups
 * the drafter rejected so the trail is auditable end-to-end.
 */
export interface EpiConceptBinding {
  conceptGroupId: string;
  conceptGroupName: string;
  status: 'selected' | 'proposed' | 'inline';
  rationale?: string;
  alternativesConsidered?: string[];
}

/**
 * Coverage section — "Does this variable cover the patients I expect?"
 * Counts framed at the cohort level so the epi can spot zero-fill /
 * missing-anchor surprises immediately.
 */
export interface EpiExecutionCoverage {
  patientsInCohort: number;
  patientsWithValue: number;
  patientsZeroFilled: number;
  patientsMissingAnchor?: number;
  patientsWithAnchor?: number;
  /** Slug of the anchor analytical variable this one depends on (e.g. `severe_asthma_index_date`). */
  anchorSlug?: string;
}

/**
 * Distribution section — "Do the values look clinically plausible?"
 * Variant per output type so the renderer can pick the right shape.
 */
export type EpiExecutionDistribution =
  | {
      kind: 'boolean';
      truePatients: number;
      falsePatients: number;
      nullPatients?: number;
    }
  | {
      kind: 'numeric';
      min: number;
      p25: number;
      median: number;
      p75: number;
      max: number;
      mean: number;
      /** Standard deviation — populated when the backend computes it. */
      stdDev?: number;
      /** Optional histogram buckets for plotting. */
      buckets?: { lower: number; upper: number; count: number }[];
    }
  | {
      kind: 'date';
      earliest: string;
      latest: string;
      /** Median date (YYYY-MM-DD) — populated when the backend computes it. */
      medianDate?: string;
      /** Year-bucketed counts. */
      buckets?: { year: number; count: number }[];
    }
  | {
      kind: 'string';
      topValues: { value: string; count: number; percentage: number | null }[];
      otherCount?: number;
      otherPercentage?: number;
      /** Number of distinct string values not shown in the top-N list. */
      otherValueCount?: number;
    };

/**
 * Concept-coverage section — "Did the bound concept groups actually find
 * anything in this dataset?" Low usedConcepts/boundConcepts ratio is a
 * heuristic flag for "you bound a lot of concepts but the data only shows
 * a handful" — worth surfacing to the epi.
 */
export interface EpiExecutionConceptUsage {
  conceptGroupId: string;
  conceptGroupName: string;
  boundConcepts: number;
  usedConcepts: number;
  /** Top-N concepts by event count contributing to this analytical variable's value. */
  topConcepts?: { conceptId: string; conceptName: string; eventCount: number }[];
}

/**
 * Technical-checks section — dev-language quarantined here, collapsed by
 * default in the UI. Lint findings get translated to epi-friendly copy
 * at the renderer.
 */
export interface EpiExecutionTechnicalChecks {
  sqlParsed: boolean;
  estimatedRuntimeSeconds?: number;
  estimatedCostCredits?: number;
  /**
   * Lint findings carry the raw rule + severity so the renderer can map
   * known false-positives ("date_filter_required" on dynamic windows) to
   * green copy and unknown rules to amber.
   */
  lintFindings: {
    rule: string;
    severity: 'error' | 'warning';
    message: string;
    /** Translated epi-language copy if the renderer recognises the rule. */
    epiCopy?: string;
  }[];
}

/**
 * Attrition funnel step — one row of the cohort attrition table that
 * the structural validator builds by walking the SQL's CTE chain. Same
 * shape as the backend's ``AttritionStep``. Surfaced on the Verification
 * tab as the "where did the cohort lose subjects" view; meaningful for
 * the researcher even when the variable isn't itself a cohort because
 * the funnel shows how the analytical variable's predicate narrows from the registry
 * spine down to the qualifying patients.
 */
export interface EpiAttritionStep {
  name: string;
  description: string;
  subjects: number;
}

/**
 * One execution of an artifact against a dataset — the audit grain.
 *
 * Every click that triggers verification produces an execution row, even
 * when the SQL+dataset+version hash matches a prior run (in which case
 * `computeMode === 'cached'` and no new SQL fires against the warehouse).
 * Records who clicked, when, against what dataset — the regulatory trail.
 *
 * The four epi-framed sections (`coverage`, `distribution`, `conceptUsage`,
 * `technicalChecks`) are populated at execution-end. While the execution
 * is in flight, `status === 'pending'` and `logs` grows in place so the
 * UI can show a live tail.
 *
 * Newest first in `EpiArtifact.executions`; the latest is rendered
 * expanded by default in the Verification tab.
 */
export interface EpiArtifactExecution {
  executionId: string;
  /** Frozen version id this execution was run against (e.g. `v3`). */
  versionId?: string;
  /** Dataset pin at execution time — the audit-relevant "what was queried". */
  datasetReleaseId: string;
  datasetReleaseDisplayName?: string;
  /** Study period resolved at execution time (YYYY-MM-DD). Present when the SQL used {study_start}/{study_end} tokens. */
  studyStart?: string;
  studyEnd?: string;
  /** Slug of the AV used as the index date source (e.g. `severe_asthma_index_date`). Mutually exclusive with `indexCalendarDate`. */
  indexAvSlug?: string;
  /**
   * OR-6433: the index anchor's durable id. Prefer this over `indexAvSlug` when
   * resolving the anchor — the alias is what it was called at verification time and
   * stops resolving once the anchor is renamed. Absent on results recorded before
   * this field existed, and on the legacy library-save path, which has no access to
   * the binding; those fall back to the alias.
   */
  indexAvId?: string;
  /** Fixed calendar date (YYYY-MM-DD) used as the index date. Mutually exclusive with `indexAvSlug`. */
  indexCalendarDate?: string;
  /** Hash of (sql, dataset, version). Cache key for `results/<hash>.json`. */
  resultHash?: string;
  /** Whether this execution recomputed or served from cache. */
  computeMode?: 'fresh' | 'cached';
  /** Actor identity from `user_info`. */
  userId?: string;
  userName?: string;
  startedAt: string;
  completedAt?: string;
  status: 'pending' | 'passed' | 'failed' | 'error' | 'empty';
  /** One-line summary, epi-language. */
  summary?: string;
  /** Streaming log buffer — markdown-rendered. Grows in place while pending. */
  logs: string;
  /** Populated at execution-end; absent while pending. */
  coverage?: EpiExecutionCoverage;
  distribution?: EpiExecutionDistribution;
  conceptUsage?: EpiExecutionConceptUsage[];
  technicalChecks?: EpiExecutionTechnicalChecks;
  /**
   * Cohort attrition funnel from the structural validator. Empty / absent
   * when the validator skipped attrition (no client) or the SQL had no
   * recognisable funnel CTEs. Rendered as a stepped bar / table on the
   * Verification tab.
   */
  attrition?: EpiAttritionStep[];
  /** Surfaced lint/parse errors with optional line numbers (jump-to-SQL hook). */
  errors?: { message: string; line?: number }[];
}

/**
 * Tabular result preview shared by Results tab and any future agent-driven
 * grid surface. Streaming-aware: the agent emits a START with columns +
 * optional summaryStats, then appends rows in chunks until END.
 *
 * Generic on purpose — boolean analytical variables surface prevalence + a 1-row table,
 * numeric analytical variables surface a histogram-style summaryStats + sample rows,
 * dataset previews surface a wide grid. The drawer doesn't care.
 */
export interface EpiArtifactResult {
  resultId: string;
  computedAt: string;
  status: 'streaming' | 'complete';
  columns: string[];
  rows: Array<Array<string | number | null>>;
  /** Total beyond preview, if known. Lets the grid show "showing 100 of 12,847". */
  rowCount?: number;
  /** Headline KPI cards rendered above the grid. */
  summaryStats?: { label: string; value: string }[];
}

/**
 * One row in a concept group's codeset. Optional fields are surfaced when
 * present; the table column set is derived from which fields are populated
 * across the codes list.
 */
export interface EpiConceptGroupCode {
  code: string;
  vocabulary: string;
  description?: string;
  conceptId?: string;
  isExcluded?: boolean;
}

/**
 * A rule-based concept group's definition body. Flat codesets omit this
 * (their codes ARE the definition); rule-based groups (SQL queries that
 * resolve to concepts, regex over descriptions, etc.) carry one of these.
 *
 * The Definition tab is only rendered when this is set — that's the gating
 * the user called out: "for any concept group that has a definition, we
 * should also have a definition tab".
 */
export interface EpiConceptGroupDefinition {
  language: 'sql' | 'markdown';
  body: string;
}

/**
 * Cross-study consumers of a concept group. Surfaced in the Usage tab so
 * the epi can see which analytical variables / cohorts in which studies depend on this
 * group before changing it.
 */
export interface EpiConceptGroupUsage {
  /** analytical variable slug or cohort id. */
  consumerId: string;
  consumerKind: 'av' | 'cohort';
  consumerLabel: string;
  studyId?: string;
  studyLabel?: string;
}
/**
 * Visibility scope of the analytical variable's KNOWLEDGE-store registration.
 * - `session`: only the session that registered it (default for new analytical variables).
 * - `user`: the author across all their sessions.
 * - `org`: shared with the author's organization.
 *
 * The backend KNOWLEDGE store also supports `omop` / `global` / `dataset`
 * scopes, but those tiers are seeded / curated — no agent-authored analytical variable
 * lives there, so the artifact view never renders them.
 */
export type EpiArtifactScope = 'session' | 'user' | 'org';

/**
 * Previewable artifact (SQL draft + manifest). Rendered in the right-side
 * slide-over and as a card in the artifact list. Keyed by id so chunked
 * streams can be reassembled in place.
 *
 * The slug-based backend layout (``avs/<slug>/draft.sql``) means a session
 * can produce many artifacts; each carries its own slug, kind (leaf analytical variable vs.
 * composition of other analytical variables), status, and the knowledge entries the drafter
 * applied. ``sources`` is a list of slugs of OTHER artifacts in the session
 * that this one anchors on or composes — empty for a leaf analytical variable.
 *
 * Older sessions (or non-analytical variable artifacts) may stream without a slug; the legacy
 * shape (``id``/``artifactType``/``name``/``content``) still renders.
 */
export interface EpiArtifact {
  id: string;
  artifactType: 'sql' | 'markdown' | 'json' | 'unknown';
  /**
   * Human-readable display label (Snowflake ``ANALYTICAL_VARIABLE.DATA_ELEMENT``;
   * backend ``ArtifactManifest.data_element``). The artifact card title. Read
   * from wire ``dataElement`` (new) falling back to ``name`` (legacy). The
   * stable id lives on ``slug`` — never show the id where this label belongs.
   */
  dataElement: string;
  content: string;
  status: 'streaming' | 'complete';
  updatedAt: string;
  /**
   * Stable id — the slug value (backend ``ArtifactManifest.name`` /
   * ``ANALYTICAL_VARIABLE.NAME``). The URL is built from it: ``/variable/av.<name>``.
   * Absent on legacy/non-analytical-variable artifacts. Distinct from ``id``
   * (which is the ``av-<name>`` artifact-list key) and ``dataElement`` (display).
   */
  name?: string;
  kind?: EpiArtifactKind;
  description?: string;
  /**
   * @deprecated Pre-rendered markdown for the Description tab. The
   * structured ``clinicalExplanation`` object is now the source of
   * truth — the Description tab renders sections from it directly. Kept
   * on the type for one release so older artifact payloads on the wire
   * still parse without a 500-error spike on the reducer; absent from
   * new emits and unused by the renderer.
   */
  explainMarkdown?: string;
  /**
   * Structured clinical breakdown — the source of truth for the
   * Description tab. One section per non-empty field, rendered in
   * order: "What this measures" → "Value" → "What counts" → "Time
   * window" → "Value selection" → "Missing values" → "What this
   * variable is NOT". Concept bindings live on a separate ``Concepts``
   * tab; the description references each concept group by name as a
   * markdown link (``[name](/concept-group/<id>)``) — bare ids are the
   * legacy/flag-off fallback, still auto-linked by the Markdown renderer.
   */
  clinicalExplanation?: EpiClinicalExplanation;
  /**
   * Compact clinical concept label (e.g. "asthma diagnosis"). The backend
   * bolds this inside the description via markdown; the UI also surfaces it
   * separately as the artifact-card emphasis.
   */
  coreLabel?: string;
  outputValueType?: EpiArtifactOutputValueType;
  /** Output kind (Snowflake ``FORMAT``; backend ``ArtifactManifest.format``). Read from wire ``format`` (new) falling back to ``outputType`` (legacy). */
  format?: EpiArtifactOutputType;
  /** Researcher-facing "Values" copy (OR-6266): numeric/free-text descriptor, or an
   *  RA-defined categorical list ("; "-joined, logical order). Empty for boolean/date
   *  (UI derives) and for pass-through categoricals (UI derives from execution topValues). */
  outputValues?: string;
  conceptGroupIds?: string[];
  /** Slugs of other artifacts this one depends on (anchor / composition). */
  sources?: string[];
  knowledgeApplied?: EpiAppliedKnowledge[];
  /**
   * Concept-group selections the drafter committed to plus the rationale
   * for each. Surfaced on the Concepts tab as the audit trail of how
   * clinical concepts were picked. Inline mentions of these ids in the
   * Description tab's prose auto-link to the concept-group details
   * dialog; this list adds the *why* behind each choice.
   */
  conceptBindings?: EpiConceptBinding[];
  /**
   * Structured term→concept mappings (OR-6018): each clinical term and the
   * concept groups OR inline OMOP concepts it resolved to. Rendered read-only
   * on the Definition tab's "Concept mappings" section (OR-6214). Saved AVs
   * carry it via the persisted manifest; drafts via the intent checkpoint
   * payload. Empty / undefined → section hides.
   */
  conceptMappings?: ConceptMapping[];
  /**
   * Drafter-time design decisions. Surfaced on the Decisions tab as a
   * flat decision/rationale table — the durable record of *why* the SQL
   * is shaped the way it is (PERSON-spine vs COHORT, time bounds, NULL
   * semantics, value-selection tie-breaks, anti-patterns avoided).
   * Empty / undefined → tab hides.
   */
  designDecisions?: EpiDesignDecision[];
  artifactStatus?: EpiArtifactStatus;
  validationBlocking?: boolean;
  /**
   * Sharing scope of the analytical variable in the KNOWLEDGE store. ``undefined`` means the
   * artifact hasn't been registered yet (still streaming / pre-finalize) —
   * surfaces in the UI as "still being worked on" rather than a controlled
   * scope chip. Once the analytical variable is registered, this is always one of
   * `session` (default) / `user` / `org`.
   */
  scope?: EpiArtifactScope;
  /** Frozen version id stamped at the most recent promotion (e.g. ``av.<slug>.v2``). */
  latestVersionId?: string;
  /**
   * Auditable executions of this artifact against a dataset. Newest first.
   * Every click that triggers verification produces an execution row — both
   * recomputes and cache hits — so the trail records every access to the
   * data, with actor + timestamp + dataset pin. The latest entry's `logs`
   * grow in place while `status === 'pending'`.
   */
  executions?: EpiArtifactExecution[];
  /**
   * Computed result previews (cohort prevalence, analytical variable sample rows, KPI
   * panels). Newest first; the latest entry's `rows` grow in place while
   * `status === 'streaming'`.
   */
  results?: EpiArtifactResult[];
  /** Concept group codeset. Set when `kind === 'concept_group'`. */
  codes?: EpiConceptGroupCode[];
  /**
   * Rule-based concept group definition. When set the drawer adds a
   * Definition tab; when absent (flat codesets) the tab is hidden.
   */
  definition?: EpiConceptGroupDefinition;
  /** Cross-study consumers of a concept group. */
  usage?: EpiConceptGroupUsage[];
}
